import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import Wordmark from "./Wordmark";

const TIERS = [
  {
    name: "Starter Partner",
    tagline: "For smaller resellers",
    price: "€999",
    period: "/month",
    highlight: false,
    features: [
      "Branded portal",
      "Custom domain",
      "Logo & colors",
      "Admin dashboard",
      "Contractor portal",
      "Up to 250 contractors",
      "Basic support",
    ],
    contractors: "Up to 250 contractors",
    cta: "Get Started",
    ctaHref: "https://nexus-vs.vawick.com/signin",
    ctaStyle: "secondary",
  },
  {
    name: "Professional Partner",
    tagline: "Most popular",
    price: "€2,500",
    period: "/month",
    highlight: true,
    features: [
      "Full white label",
      "Unlimited organizations",
      "Advanced analytics",
      "API access",
      "Workflow customization",
      "Priority support",
    ],
    contractors: "1,000 – 2,500 contractors",
    cta: "Get Started",
    ctaHref: "https://nexus-vs.vawick.com/signin",
    ctaStyle: "primary",
  },
  {
    name: "Enterprise Partner",
    tagline: "Payroll companies · Consultancies · HR providers",
    price: "From €5,000",
    period: "/month",
    highlight: false,
    features: [
      "Dedicated environment",
      "Custom integrations",
      "SLA guarantee",
      "Onboarding support",
      "Security documentation",
      "Custom features",
    ],
    contractors: "Unlimited contractors",
    cta: "Talk to us",
    ctaHref: "mailto:support@vawick.de",
    ctaStyle: "secondary",
  },
];

function TierCard({ tier, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`relative flex flex-col rounded-2xl p-8 ${
        tier.highlight
          ? "bg-[#FF4F00] text-white ring-2 ring-[#FF4F00] shadow-[0_0_60px_rgba(255,79,0,0.25)]"
          : "glass border border-[#152222] text-white"
      }`}
    >
      {tier.highlight && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="bg-white text-[#FF4F00] text-xs font-semibold px-4 py-1 rounded-full tracking-wide">
            MOST POPULAR
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h3 className={`text-lg font-semibold mb-1 ${tier.highlight ? "text-white" : "text-white"}`}>
          {tier.name}
        </h3>
        <p className={`text-sm ${tier.highlight ? "text-orange-100" : "text-[#9BB8B8]"}`}>
          {tier.tagline}
        </p>
      </div>

      {/* Price */}
      <div className="mb-8">
        <span className="text-4xl font-bold tracking-tight">{tier.price}</span>
        <span className={`text-sm ml-1 ${tier.highlight ? "text-orange-100" : "text-[#9BB8B8]"}`}>
          {tier.period}
        </span>
        <p className={`text-xs mt-2 font-mono ${tier.highlight ? "text-orange-100" : "text-[#7A9B9B]"}`}>
          {tier.contractors}
        </p>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <Check
              className={`w-4 h-4 mt-0.5 shrink-0 ${tier.highlight ? "text-white" : "text-[#FF4F00]"}`}
            />
            <span className={tier.highlight ? "text-white" : "text-[#A8BFBF]"}>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href={tier.ctaHref}
        className={`group inline-flex items-center justify-center gap-2 w-full py-3 rounded-full font-medium text-sm transition-all duration-300 ${
          tier.highlight
            ? "bg-white text-[#FF4F00] hover:bg-[#0B1414] hover:text-white"
            : "border border-[#1F3535] hover:border-[#FF4F00] hover:bg-[#FF4F00] hover:text-white"
        }`}
      >
        {tier.cta}
        {tier.ctaStyle === "primary" ? (
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        ) : (
          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        )}
      </a>
    </motion.div>
  );
}

export default function Partners() {
  return (
    <div className="min-h-screen relative overflow-x-hidden" data-testid="partners-page">
      {/* Background */}
      <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] glow-vermilion opacity-30 pointer-events-none" aria-hidden />

      {/* Minimal nav */}
      <header className="relative z-10 pt-8 pb-4 px-6 lg:px-12 max-w-[1200px] mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <Wordmark size="lg" />
        </Link>
        <a
          href="mailto:support@vawick.de"
          className="text-sm text-[#9BB8B8] hover:text-white transition-colors sm:text-right"
        >
          Questions? <span className="text-[#7A9B9B]">support@vawick.de</span>
        </a>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 pt-16 pb-24">
        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-xs text-[#9BB8B8] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4F00] pulse-dot shrink-0" />
            <span className="font-mono tracking-[0.12em]">White-label Partnership Programme</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-[-0.04em] leading-[1.05] mb-5">
            Resell nexus-vs<br />
            <span className="italic font-normal text-[#9BB8B8]">under your brand</span>
          </h1>
          <p className="text-[#9BB8B8] text-lg max-w-xl mx-auto leading-relaxed">
            Deploy a fully branded contractor management platform for your clients.
            Your logo, your domain, your revenue.
          </p>
        </motion.div>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {TIERS.map((tier, i) => (
            <TierCard key={tier.name} tier={tier} index={i} />
          ))}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-xs text-[#7A9B9B] font-mono mt-12"
        >
          All plans billed monthly · Minimum 3-month commitment · Setup fee waived for annual contracts
        </motion.p>
      </main>
    </div>
  );
}
